import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { 
  TrendUp, 
  TrendDown, 
  Lock,
  ArrowRight,
  ChartLine
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Trading = () => {
  const { user, isAuthenticated } = useAuth();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSignals = async () => {
      try {
        const response = await api.get('/signals');
        setSignals(response.data);
      } catch (error) {
        toast.error('Failed to load trading signals');
      } finally {
        setLoading(false);
      }
    };

    fetchSignals();
  }, []);

  const getTierBadgeClass = (tier) => {
    if (tier === 'elite') return 'bg-[#D4AF37]/20 text-[#D4AF37]';
    if (tier === 'premium') return 'bg-white/10 text-white';
    return 'bg-zinc-800 text-zinc-400';
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="trading-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-2">
            Trading <span className="text-[#D4AF37]">Hub</span>
          </h1>
          <p className="text-zinc-400">Live charts and trading signals from our expert analysts</p>
        </motion.div>

        {/* TradingView Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#121212] border border-white/5 p-4 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-xl text-white flex items-center gap-2">
              <ChartLine className="w-5 h-5 text-[#D4AF37]" weight="duotone" />
              Live Chart - BTC/USD
            </h2>
          </div>
          <div className="w-full h-[500px] bg-[#050505]">
            <iframe
              src="https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&symbol=BINANCE%3ABTCUSDT&interval=D&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=050505&studies=%5B%5D&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%5D&showIntervalTabs=true&locale=en"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="TradingView Chart"
            />
          </div>
        </motion.div>

        {/* Trading Signals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-outfit text-2xl text-white">Trading Signals</h2>
            {!isAuthenticated && (
              <Link to="/login">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                  Login to View All
                </Button>
              </Link>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#121212] border border-white/5 p-6 animate-pulse">
                  <div className="h-6 bg-zinc-800 rounded w-1/2 mb-4" />
                  <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-zinc-800 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {signals.map((signal, index) => (
                <motion.div
                  key={signal.signal_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-[#121212] border p-6 card-hover relative ${
                    signal.is_locked ? 'border-white/5' : 'border-white/10'
                  }`}
                  data-testid={`signal-card-${signal.symbol}`}
                >
                  {signal.is_locked && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                      <Lock className="w-8 h-8 text-[#D4AF37] mb-2" weight="duotone" />
                      <p className="text-sm text-zinc-400 mb-3">Upgrade to unlock</p>
                      <Link to="/pricing">
                        <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                          Upgrade <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-lg text-white font-medium">{signal.symbol}</span>
                    {signal.action === 'BUY' ? (
                      <span className="flex items-center gap-1 text-green-500 text-sm font-medium">
                        <TrendUp className="w-4 h-4" weight="bold" />
                        BUY
                      </span>
                    ) : signal.action === 'SELL' ? (
                      <span className="flex items-center gap-1 text-red-500 text-sm font-medium">
                        <TrendDown className="w-4 h-4" weight="bold" />
                        SELL
                      </span>
                    ) : (
                      <span className="text-zinc-400 text-sm font-medium">HOLD</span>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Entry</span>
                      <span className="font-mono text-white">
                        {signal.entry_price ? `$${signal.entry_price.toLocaleString()}` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Target</span>
                      <span className="font-mono text-green-500">
                        {signal.target_price ? `$${signal.target_price.toLocaleString()}` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Stop Loss</span>
                      <span className="font-mono text-red-500">
                        {signal.stop_loss ? `$${signal.stop_loss.toLocaleString()}` : '—'}
                      </span>
                    </div>
                  </div>

                  {signal.analysis && !signal.is_locked && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <p className="text-xs text-zinc-400">{signal.analysis}</p>
                    </div>
                  )}

                  <div className="mt-4">
                    <span className={`text-xs font-mono uppercase px-2 py-1 ${getTierBadgeClass(signal.tier_required)}`}>
                      {signal.tier_required}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Upgrade CTA for non-elite users */}
        {isAuthenticated && user?.subscription_tier !== 'elite' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-gradient-to-r from-[#D4AF37]/20 to-[#B38F24]/20 border border-[#D4AF37]/30 p-8 text-center"
          >
            <h3 className="font-outfit text-2xl text-white mb-2">
              Get All Trading Signals
            </h3>
            <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
              Upgrade to Premium or Elite to access all trading signals with detailed analysis and alerts.
            </p>
            <Link to="/pricing">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] px-8 py-6 gold-glow">
                View Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Trading;
